import type { ENUM_ASSET_STATUS } from "@/utils/enums";
import type { IUser } from "../(my-space)/users.interface";
import type {
  ISingleAssetType,
  ISingleConsumableAsset,
} from "../(my-space)/assets.interface";

export interface IAssetStatsResponse {
  totalAssets: number;
  inUseAssets: number;
  availableAssets: number;
  maintenanceAssets: number;
  tabCounts: {
    pendingAssetRequests: number;
  };
}

export interface IAssetRequestStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  fulfilled: number;
  cancelled: number;
}

export interface IAssetType {
  id: number;
  name: string;
  isActive: boolean;
  companyId: number;
  createdAt: string;
  updatedAt: string;
}

export interface ICompanyGetAssetsInventoryParams {
  page?: number;
  limit?: number;
  search?: string;
  assetTypeId?: number;
  brandId?: number;
  status?: string;
  branchId?: number;
}

export interface ICompanySingleInventoryEntry {
  assetTypeId: number;
  typeName: string;
  totalAssets: string;
  assignedAssets: number;
  availableAssets: number;
  damagedAssets: number;
  lostAssets: number;
  purchaseDate: string;
}

export enum EnumAssetFileTypes {
  assetImage = "asset-image",
  assetInvoice = "asset-invoice",
}
export interface IAssetFile {
  id: number;
  fileUrl: string;
  assetId: number;
  updatedAt: string;
  createdAt: string;
  isPrimary: boolean;
  uploadedAt: string;
  fileType: EnumAssetFileTypes;
}

export interface IAssetHistoryFile {
  id: number;
  assetHistoryId: number;
  fileType: string;
  fileUrl: string;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface IAssetHistoryUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string | null;
}

export interface IAssetHistoryEntry {
  id: number;
  assetId: number;
  companyId: number;
  action: string;
  userId: number | null;
  performedBy: number;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  comment: string | null;
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
  user: IAssetHistoryUser | null;
  performer: IAssetHistoryUser | null;
  files: IAssetHistoryFile[];
}
export interface ISingleAsset {
  id: number;
  brandId: number;
  branchId: number | null;
  branch: { id: number; name: string; code: string } | null;
  assetTag: string;
  createdAt: string;
  updatedAt: string;
  osVersion: string;
  companyId: number;
  description: string;
  assetTypeId: number;
  serialNumber: string;
  purchaseDate: string;
  licenseSerial: string;
  modelSoftware: string;
  emailId: string | null;
  username: string | null;
  password: string | null;
  status: ENUM_ASSET_STATUS;
  assignedTo: number | null;
  retireReason: string | null;
  assignedDate: string | null;
  assetReview: string | null;
  isRequestedForReplace: boolean;
  expirationDate: string | null;
  unassignReason: string | null;
  primaryImageId: number | null;
  assignedUser?: {
    id: number;
    email: string;
    lastName: string;
    firstName: string;
    profileImage?: string | null;
  };
  assetType: {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
    createdAt: string;
    updatedAt: string;
  };
  brand: {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
    createdAt: string;
    updatedAt: string;
  };
  files: IAssetFile[];
  primaryImage: IAssetFile | null;
  history?: IAssetHistoryEntry[];
}

export interface IConsumableUnitType {
  id: number;
  name: string;
}

export interface IConsumableInventoryItem {
  unitTypeId: number;
  unitCount: number;
  itemsPerUnit: number;
}

export interface ICreateConsumableAssetPayload {
  name: string;
  consumableCategoryId: number;
  branchId: number;
  inventory: IConsumableInventoryItem[];
}
export interface IConsumableInventoryEntry {
  id: number;
  consumableId: number;
  unitTypeId: number;
  unitCount: number;
  itemsPerUnit: number;
  totalItems: number;
  issuedItems: number;
  issuedUnits:number;
  stockLeft: number;
  stockUnitsLeft:number;
  unitType: { id: number; name: string };
}

export interface IConsumableAsset {
  id: number;
  consumableTag: string;
  name: string;
  consumableCategoryId: number;
  branchId: number | null;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  consumableCategory: IConsumableAssetCategory;
  inventory: IConsumableInventoryEntry[];
  totalItems: number;
}

export interface IApprovalStepUser {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  employeeId?: string;
  email?: string;
  companyEmail?: string;
  companyPhoneNumber?: string;
  department?: string;
  position?: string;
  branch?: string;
}

export interface IApprovalStep {
  stepOrder: number;
  stepName: string;
  // Graph node kind. "parallel_approval" → render the `approvers` set.
  type?: string | null;
  positionName: string;
  approver: IApprovalStepUser | null;
  approvers?:
    | (IApprovalStepUser & {
        role?: string | null;
        status: string;
        actedAt?: string | null;
      })[]
    | null;
  branch: string | null;
  status: string;
  isCurrent: boolean;
  actionAt: string | null;
  comments: string | null;
}

export interface IAssetApprovalSteps {
  startingPoint: {
    requester: IApprovalStepUser;
    condition: {
      assetTypeName: string;
      requestType: string;
      description: string;
    };
  };
  totalSteps: number;
  currentStep: number;
  instanceStatus: string;
  steps: IApprovalStep[];
}

export interface ISingleAssetRequest {
  id: number;
  notes: null;
  user: IUser;
  userId: number;
  rejector: null;
  status: string;
  approver: null;
  priority: string;
  approvedAt: null;
  companyId: number;
  rejectedAt: null;
  requestId: string;
  fulfilledAt: null;
  assetTypeId: number;
  attachmentUrl: null;
  assetCategory: string;
  rejectionReason: null;
  reason: string | null;
  assetId: string | null;
  updatedAt: string | null;
  createdAt: string | null;
  approvedBy: string | null;
  oldAssetId: number | null;
  rejectedBy: string | null;
  requestDate: string | null;
  description: string | null;
  requestType: string | null;
  asset: ISingleAsset | null;
  requiredDate: string | null;
  consumableId: number | null;
  assetType: ISingleAssetType | null;
  consumableCategory: string | null;
  consumableRequiredQuantity: number | null;
  consumable: ISingleConsumableAsset | null;
  availableInventory:IConsumableInventoryEntry[] | null;
  requestedQuantity?:number;
  // Active approver of the workflow instance for this asset request.
  // Used to gate Approve/Reject action buttons to only the current
  // approver. Null when no workflow instance exists.
  currentApproverId?: number | null;
  workflowInstanceStatus?: string | null;
}
export interface IAssetRequestData {
  assetRequest?: ISingleAssetRequest;
  approvalSteps?: IAssetApprovalSteps;
}

export interface IConsumableAssetCategory {
  id: number;
  name: string;
  code: string;
  comapnyId: number;
}

export interface IAssigneesListFilters {
  fromDate: Date | null;
  toDate: Date | null;
  consumableCategoryId: string | null;
}

export interface IIssuedUser {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  employmentDetails: {
    employeeId: string;
  };
  employeeName: string;
  employeeId: string;
}

export interface IConsumable {
  id: number;
  name: string;
  consumableTag: string;
  companyId: number;
  consumableCategoryId: number;
  unit: string;
  consumableCategory: {
    id: number;
    name: string;
  };
}

export interface IConsumableAssignees {
  id: number;
  consumableId: number;
  quantity: number;
  notes: string;
  issuedTo: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  issuedUser: IIssuedUser;
  consumable: IConsumable;
}

export interface IAssetRequestFilters {
  assetTypeId: string;
  assetCategory: string;
  priority: string;
  requestDateFrom: Date | null;
  requestDateTo: Date | null;
  status: string;
}
